import React, { useEffect, useState } from "react";
import {
  ComposedChart,
  Area,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
  Label,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { CustomLegend } from "../Custom/CustomLegendContent";
import { getL1SheetProperty } from "../../../features/reduxworkbook/WorkbookSlice";
import { getColourSchemeByID } from "../../../Colors/ColorPalettes";
import { formatNumber } from "../Utils/FormatNumbers";
import { CustomTooltip } from "../Custom/CustomTooltipContent";
import { castToNumberData } from "../Utils/transformData";
import {
  dispatchSortValues,
  stackedBarChartPosition,
  calculateYforXaxisLabel,
} from "../Utils/ChartsFunctions";
import { setSheetControls } from "../../../features/reduxworkbook/WorkbookControllingSlice";
import { sortData } from "../Utils/SortData";
import { rightAxisVisible } from "../Utils/calculateAxis";

const sortValueKeys = (valueKeys) => {
  if (valueKeys.length < 2) {
    return valueKeys;
  }

  const customOrder = ["area", "bar", "line"];
  // console.log(valueKeys);

  valueKeys.sort((a, b) => {
    // console.log(a.display, b.display);

    const indexA = customOrder.indexOf(a.display);
    const indexB = customOrder.indexOf(b.display);

    if (indexA !== -1 && indexB !== -1) {
      // If both elements are in the custom order, sort by their order
      return indexA - indexB;
    } else if (indexA !== -1) {
      // If only A is in the custom order, it comes first
      return -1;
    } else if (indexB !== -1) {
      // If only B is in the custom order, it comes first
      return 1;
    } else {
      // If neither is in the custom order, sort alphabetically
      return a.display.localeCompare(b.display);
    }
  });

  return valueKeys; // Ensure the sorted array is returned
};

const Combochart = ({
  Data: incomingData,
  Sheetid,
  rowKeys: rawRowkeys,
  toolTipKeys = [],
  groupByKeys,
  valueKeys = [],
  sortProperties,
}) => {
  // console.log(toolTipKeys);

  const [hoverIndex, setHoverIndex] = useState(null);
  const [longestLabelWidth, setLongestLabelWidth] = useState(0);
  const customizationObject = useSelector((state) =>
    getL1SheetProperty(state, Sheetid, "customization")
  );
  const dispatch = useDispatch();
  const rowKeys = rawRowkeys ? rawRowkeys.map((item) => item.responseKey) : [];
  const responseKeys = valueKeys.map((item) => item.responseKey);
  const Data = castToNumberData(incomingData, responseKeys);
  const SufixPrefix = { ...valueKeys.map((item) => item.numberFormat)[0] };

  // const xa = rawGroupByKeys[0];
  const xa = groupByKeys[0];
  const yb = valueKeys[0];
  //y axes dual
  const ybDual = valueKeys[1];
  const ybiax = valueKeys[1];
  const xAxisResponseKey = xa?.columnName;
  const yAxisCaption = yb?.caption;
  const ybiaXialCaption = ybiax?.caption;
  const ybdualaxesCaption = ybDual?.caption;
  // console.log(xa, yb, ybiax, xAxisResponseKey, yAxisCaption, ybiaXialCaption);
  // console.log(ybdualaxesCaption);

  const {
    colorSchemeid,
    ComboLinepointSize,
    isComboBarLabelVisible,
    isComboLineLabelVisible,
    labelComboBarPosition,
    labelComboBarFontSize,
    labelComboBarFontColor,
    labelComboBarRotate,
    labelComboLinePosition,
    labelComboLineFontSize,
    labelComboLineFontColor,
    labelComboLineRotate,
    isComboAreaLabelVisible,
    labelComboAreaPosition,
    labelComboAreaFontSize,
    labelComboAreaFontColor,
    labelComboAreaRotate,
    colorMapping,
    isAxeslabelVisible,
    labelAxesFontColor,
    labelAxesFontSize,
    isLegendVisible,
    legendFontColor,
    isLegendBold,
    legendAlign,
    legendVerticalAlign,
    legendLayout,
    isTitleBold,
    Title,
    isTileVisible,
    TitleFontColor,
    TitleSize,
    valueAlign,
  } = customizationObject;
  // console.log(colorMapping);

  // console.log(
  //   isLegendVisible,
  //   legendFontColor,
  //   isLegendBold,
  //   legendAlign,
  //   legendVerticalAlign,
  //   legendLayout
  // );
  const newArray =
    valueKeys.length ? valueKeys.map((vItem) => vItem.responseKey) : [];
  useEffect(() => {
    // console.log("newArray", newArray);
    if (groupByKeys.length) {
      dispatchSortValues({
        newArray,
        dispatch,
        setSheetControls,
        Sheetid,
      });
    }
  }, [newArray]);
  const sorteData = sortData({
    transformedData: Data,
    sortProperties,
    requiredArray: groupByKeys,
  });
  const colorScheme = getColourSchemeByID(colorSchemeid);
  const COLORS = colorScheme?.colors;
  // console.log(sorteData);
  const barSize = customizationObject.barSize;
  const isGridVisible = customizationObject.isGridVisible;
  const isTransposeVisible = customizationObject.isTransposeVisible;
  const isLabelVisible = customizationObject.isLabelVisible;
  const labelPosition = customizationObject.labelPosition;
  const labelFontSize = customizationObject.labelFontSize;
  const labelFontColor = customizationObject.labelFontColor;
  const labelRotate = customizationObject.labelRotate;

  const sortedValueKeys = sortValueKeys(valueKeys);
  // console.log(sortedValueKeys);

  // const tickFormatter = (tick) => {
  //   if (typeof tick === "number") {
  //     return formatNumber(tick, SufixPrefix);
  //   }
  //   return tick;
  // };
  const isRightAxisVisible = rightAxisVisible({ valueKeys });
  // console.log(isRightAxisVisible);

  const tickFormatter = (tick) => {
    // console.log(SufixPrefix.mode === "Manual");
    // console.log(!isTransposeVisible);

    if (typeof tick === "number" || typeof tick === "string") {
      const formattedTick =
        typeof tick === "string" ? tick : formatNumber(tick, SufixPrefix);

      // Create a temporary canvas to measure the text width
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      context.font = "12px Arial"; // Use the same font as the chart
      const textWidth = context.measureText(formattedTick).width;

      // Update the longest label width if this tick is longer
      // Update the longest label width if this tick is longer
      if (isTransposeVisible) {
        setLongestLabelWidth(
          (prevWidth) => Math.max(prevWidth, textWidth) - 20
        );
      } else {
        if (SufixPrefix.mode === "Manual") {
          setLongestLabelWidth(
            (prevWidth) => Math.max(prevWidth, textWidth) - 20
          );
        } else {
          setLongestLabelWidth(
            (prevWidth) => Math.max(prevWidth, textWidth) - 10
          );
        }
      }

      return formattedTick;
    }
    return tick;
  };

  const handleComboMouseEnter = (lineIndex) => {
    setHoverIndex(lineIndex);
  };

  const handleComboMouseLeave = () => {
    setHoverIndex(null);
  };

  const { left, top, bottom, right } = stackedBarChartPosition(
    isAxeslabelVisible,
    isTransposeVisible,
    SufixPrefix,
    0,
    0,
    false,
    longestLabelWidth,
    {}
  );
  // YAXIS axes label right and left
  const getYAxisLabelContent = ({ viewBox }, axis) => {
    const { x, y, height } = viewBox;

    const centerY = y + height / 2;

    const centerX =
      axis === "right"
        ? isTransposeVisible
          ? x - 30
          : x + 60
        : isTransposeVisible
        ? 30 // <-- Updated this for left axis when transposed
        : 15; // Default for left axis when not transposed

    let labelText;

    if (axis === "right") {
      // Right Y-axis label
      labelText = isTransposeVisible ? yAxisCaption : ybiaXialCaption;
      // console.log(yAxisCaption, xAxisResponseKey);
    } else {
      // Left Y-axis label
      if (isTransposeVisible) {
        labelText = xAxisResponseKey;
        // console.log(labelText);
      } else {
        if (isRightAxisVisible) {
          // labelText = xAxisCaption || xAxisCaption;
          labelText = yAxisCaption;
          // console.log(xAxisResponseKey, yAxisCaption);
        } else {
          labelText = xAxisResponseKey
            ? ybiaXialCaption
              ? `${yAxisCaption} / ${ybiaXialCaption}`
              : yAxisCaption
            : ybiaXialCaption ?? null;
        }
      }
    }

    return (
      <text
        x={centerX}
        y={centerY}
        textAnchor="middle"
        dominantBaseline={isTransposeVisible ? "middle" : "hanging"}
        fill={labelAxesFontColor}
        style={{
          fontWeight: "normal",
          fontSize: labelAxesFontSize,
        }}
        transform={`rotate(-90, ${centerX}, ${centerY})`}
      >
        {labelText}
      </text>
    );
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <div style={{ width: "100%", height: isTileVisible ? "5%" : "0%" }}>
        {isTileVisible && (
          <div
            className={`w-auto ${isTitleBold ? "font-bold" : ""} ${
              valueAlign === "left"
                ? "text-left"
                : valueAlign === "right"
                ? "text-right"
                : "text-center"
            }`}
            style={{
              fontSize: TitleSize,
              color: TitleFontColor ?? "#bababa",
            }}
          >
            {Title || " Title..."}
          </div>
        )}
      </div>
      <div style={{ width: "100%", height: isTileVisible ? "95%" : "100%" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={sorteData}
            layout={isTransposeVisible ? "vertical" : "horizontal"}
            margin={{
              left: left,
              bottom,
              top,
              right,
            }}
          >
            <defs>
              <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={COLORS} stopOpacity={0.8} />
                <stop offset="95%" stopColor={COLORS} stopOpacity={0} />
              </linearGradient>
            </defs>

            {/* Optional CartesianGrid */}
            {isGridVisible && <CartesianGrid strokeDasharray="3 3" />}

            {/* X and Y Axis */}
            <XAxis
              // dataKey={groupByKeys[0].responseKey}
              type={isTransposeVisible ? "number" : "category"}
              dataKey={isTransposeVisible ? null : groupByKeys[0].responseKey}
              tickFormatter={isTransposeVisible ? tickFormatter : null}
              label={
                isAxeslabelVisible
                  ? {
                      content: ({ viewBox }) => {
                        const { x, y, width, height } = viewBox;

                        const centerX = x + width / 2;
                        // console.log(centerX);

                        // const centerY = y + height / 2;
                        const centerY = calculateYforXaxisLabel({
                          isTransposeVisible,

                          x,
                          y,
                          width,
                          height,
                          isAxeslabelVisible,
                        });
                        // console.log(centerY);
                        // console.log(centerY);

                        return (
                          <>
                            <text
                              x={centerX}
                              y={centerY}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill={labelAxesFontColor}
                              style={{
                                fontWeight: "normal",
                                fontSize: labelAxesFontSize,
                              }}
                              transform={
                                isTransposeVisible
                                  ? null
                                  : `rotate(0, ${centerX}, ${566})`
                              }
                            >
                              {/* {isTransposeVisible ? yAxisCaption : xAxisResponseKey} */}
                              {isTransposeVisible
                                ? yAxisCaption && ybiaXialCaption
                                  ? `${yAxisCaption} / ${ybiaXialCaption}`
                                  : yAxisCaption
                                : xAxisResponseKey}
                            </text>
                          </>
                        );
                      },
                    }
                  : null
              }
            >
              <Label offset={-5} position="insideBottom" />
            </XAxis>
            {/* independentAxis */}
            <YAxis
              // yAxisId="left"
              // type={isTransposeVisible ? "category" : "number"}
              // dataKey={isTransposeVisible ? rowKeys[0] : null}
              yAxisId="left"
              type={isTransposeVisible ? "category" : "number"}
              dataKey={isTransposeVisible ? groupByKeys[0].responseKey : null}
              tickFormatter={tickFormatter}
              label={
                isAxeslabelVisible
                  ? {
                      content: (viewBox) =>
                        getYAxisLabelContent(viewBox, "left"),
                    }
                  : null
              }
            >
              <Label angle={-90} position="insideLeft" />
            </YAxis>
            {isRightAxisVisible && !isTransposeVisible && (
              <YAxis
                yAxisId="right"
                orientation="right"
                type={isTransposeVisible ? "category" : "number"}
                dataKey={isTransposeVisible ? rowKeys[0] : null}
                tickFormatter={tickFormatter}
                label={
                  isAxeslabelVisible
                    ? {
                        content: (viewBox) =>
                          getYAxisLabelContent(viewBox, "right"),
                      }
                    : null
                }
              ></YAxis>
            )}

            <Tooltip
              content={
                <CustomTooltip
                  chartType={"combochart"}
                  tooltipKeys={toolTipKeys}
                  SufixPrefix={SufixPrefix}
                  colorSchemeid={colorSchemeid}
                  columnKeys={groupByKeys}
                  valueKeys={valueKeys}
                  currentLineIndex={hoverIndex}
                  colorMapping={colorMapping}
                />
              }
            />
            {isLegendVisible && (
              <Legend
                iconType="rect"
                align={legendAlign?.value}
                verticalAlign={legendVerticalAlign?.value}
                layout={legendLayout?.value}
                content={
                  <CustomLegend
                    {...{
                      align: legendAlign?.value,
                      verticalAlign: legendVerticalAlign?.value,
                      layout: legendLayout?.value,
                      valueKeys,
                      legendFontColor,
                      isLegendBold,
                    }}
                  />
                }
              />
            )}

            {/* Render different chart types based on valueKeys */}
            {sortedValueKeys.map((key, index) => {
              const baseColor =
                colorMapping?.[key?.responseKey] ||
                COLORS[index % COLORS.length];

              const numberFormatObject = key.numberFormat;
              const independentKey = key.independentAxis;
              // console.log(independentKey);

              const axisId = independentKey === true ? "right" : "left";
              // console.log(axisId);

              // // console.log(requiredArrayOfQuery[index].independentAxis);

              if (key.display === "bar") {
                // Render regular Bar chart
                return (
                  <Bar
                    key={`regular-bar-${index}`}
                    dataKey={key.responseKey}
                    yAxisId={axisId}
                    fill={baseColor}
                    // fill={
                    // [index % COLORS.length]}
                    barSize={barSize}
                    onMouseEnter={() => handleComboMouseEnter(index)}
                    onMouseLeave={handleComboMouseLeave}
                  >
                    {/* label only bar chart  only */}
                    {isComboBarLabelVisible && (
                      <LabelList
                        dataKey={key.responseKey}
                        position={labelComboBarPosition?.value}
                        fontSize={labelComboBarFontSize}
                        fill={labelComboBarFontColor}
                        angle={labelComboBarRotate}
                        //{label || "your text here..."}

                        formatter={(value) =>
                          formatNumber(value, numberFormatObject)
                        }
                      />
                    )}

                    {/* combochart  */}
                    {/* {isLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelPosition}
                    fontSize={labelFontSize}
                    fill={labelFontColor}
                    formatter={(value) => formatNumber(value, numberFormatObject)}
                  />
                )} */}
                  </Bar>
                );
              }
              if (key.display === "area") {
                // Render Area chart
                return (
                  <Area
                    key={`area-chart-${index}`}
                    type="monotone"
                    dataKey={key.responseKey}
                    yAxisId={axisId}
                    // stroke={COLORS[index % COLORS.length]}
                    stroke={baseColor}
                    fillOpacity={0.5}
                    // fill={COLORS[index % COLORS.length]}
                    fill={baseColor}
                    onMouseEnter={() => handleComboMouseEnter(index)}
                    onMouseLeave={handleComboMouseLeave}
                  >
                    {isComboAreaLabelVisible && (
                      <LabelList
                        dataKey={key.responseKey}
                        position={labelComboAreaPosition?.value}
                        fontSize={labelComboAreaFontSize}
                        fill={labelComboAreaFontColor}
                        angle={labelComboAreaRotate}
                        formatter={(value) =>
                          formatNumber(value, numberFormatObject)
                        }
                      />
                    )}
                    {/* //are combochart */}
                    {/* {isLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelPosition}
                    fontSize={labelFontSize}
                    fill={labelFontColor}
                    formatter={(value) => formatNumber(value, numberFormatObject)}
                  />
                )} */}
                  </Area>
                );
              }

              if (key.display === "line") {
                // Render Line chart
                return (
                  <Line
                    key={`line-chart-${index}`}
                    type="monotone"
                    dataKey={key.responseKey}
                    // stroke={getColor("line")}
                    yAxisId={axisId}
                    // stroke={COLORS[index % COLORS.length]}
                    stroke={baseColor}
                    strokeWidth={2}
                    onMouseEnter={() => handleComboMouseEnter(index)}
                    onMouseLeave={handleComboMouseLeave}
                    dot={
                      hoverIndex === index
                        ? {
                            r: ComboLinepointSize + 1, // Bigger dot on hover
                            onMouseEnter: () => handleComboMouseEnter(index),
                            onMouseLeave: handleComboMouseLeave,
                          }
                        : false
                    }
                  >
                    {isComboLineLabelVisible && (
                      <LabelList
                        dataKey={key.responseKey}
                        position={labelComboLinePosition?.value}
                        fontSize={labelComboLineFontSize}
                        fill={labelComboLineFontColor}
                        angle={labelComboLineRotate}
                        formatter={(value) =>
                          formatNumber(value, numberFormatObject)
                        }
                      />
                    )}
                    {/* combo line label */}
                    {/* {isLabelVisible && (
                  <LabelList
                    dataKey={key.responseKey}
                    position={labelPosition}
                    fontSize={labelFontSize}
                    fill={labelFontColor}
                    formatter={(value) => formatNumber(value, numberFormatObject)}
                  />
                )} */}
                  </Line>
                );
              }

              if (key.display === "stackbar") {
                // Render stacked bars
                return (
                  <>
                    <Bar
                      key={`stacked-bar1-${index}`}
                      dataKey={key.responseKey}
                      stackId="a"
                      // fill={COLORS[index % COLORS.length]}
                      fill={baseColor}
                      barSize={barSize}
                      onMouseEnter={() => handleComboMouseEnter(index)}
                      onMouseLeave={handleComboMouseLeave}
                    >
                      {isLabelVisible && (
                        <LabelList
                          dataKey={key.responseKey}
                          position={labelPosition}
                          fontSize={labelFontSize}
                          fill={labelFontColor}
                          angle={labelRotate}
                        />
                      )}
                    </Bar>
                    <Bar
                      key={`stacked-bar2-${index}`}
                      dataKey={key.responseKey}
                      stackId="a"
                      // fill={COLORS[index % COLORS.length]}
                      fill={baseColor}
                      barSize={barSize}
                      onMouseEnter={() => handleComboMouseEnter(index)}
                      onMouseLeave={handleComboMouseLeave}
                    >
                      {isLabelVisible && (
                        <LabelList
                          dataKey={key.responseKey}
                          position={labelPosition}
                          fontSize={labelFontSize}
                          fill={labelFontColor}
                          angle={labelRotate}
                        />
                      )}
                    </Bar>
                  </>
                );
              }

              return null; // Return null for unsupported chart types
            })}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Combochart;
